import {useTranslation} from "react-i18next";
import classNames from "classnames";
import {withStyles} from "@mui/styles";
import {connect} from "react-redux";
import {Alert, Button, Card, CardActions, CardContent, Grid, Typography, FormControl, InputLabel, Select, MenuItem, CircularProgress, Tabs, Tab, Box, Chip, List, ListItem, ListItemIcon, ListItemText, useTheme} from "@mui/material";
import {Profile} from "../User/userDTO";
import {Fragment, useEffect, useState} from "react";
import styles from "./PlanPage-jss";
import {Dispatch} from "redux";
import Page from "../../components/Page/Page";
import {GrConfigure} from "react-icons/gr";
import live4tvPlanService, {Subscription, Plan, PlanProduct} from "./live4tvPlanService";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import useGTM from "../../packages/useGTM/useGTM";

const stripe = require('config/app.config').stripe;

type Properties = {
    classes: any,
    profile: Profile,
}

function PlanPage(props: Properties) {
    const {
        classes,
        profile,
    } = props;

    const [activePlans, setActivePlans] = useState<Array<Subscription>>([]);
    const [planProducts, setPlanProducts] = useState<Array<PlanProduct>>([]);
    const [allPlanProducts, setAllPlanProducts] = useState<Array<PlanProduct>>([]);
    const [hasPreviousSubscriptions, setHasPreviousSubscriptions] = useState<boolean>(false);
    const [selectedCurrency, setSelectedCurrency] = useState<string>('');
    const [selectedBillingCycle, setSelectedBillingCycle] = useState<string>('monthly');
    const [availableCurrencies, setAvailableCurrencies] = useState<Array<string>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [planLoading, setPlanLoading] = useState<boolean>(false);

    const {t} = useTranslation();
    const theme = useTheme();
    const gtm = useGTM({profile});

    useEffect(() => {
        const initializePlans = async () => {
            // First check for active plans
            try {
                const activePlansResponse = await live4tvPlanService.getActivePlan(profile.token);
                setActivePlans(activePlansResponse.data);

                // Only fetch currencies if no active plans
                if (activePlansResponse.data.length === 0) {
                    await fetchAllCurrencies();
                }
            } catch (e) {
                // If check fails, still try to fetch currencies
                await fetchAllCurrencies();
            }
        };

        initializePlans();
    }, []);

    const checkActivePlan = async () => {
        try {
            const activePlans = await live4tvPlanService.getActivePlan(profile.token);
            setActivePlans(activePlans.data);
        } catch (e) {}
    }

    const fetchAllCurrencies = async (retryCount = 0) => {
        try {
            // Fetch all plan products without currency or billing cycle filter to get all available currencies and plans
            const response = await live4tvPlanService.getPlanProducts(profile.token, undefined, undefined);
            const products = response.data.planProducts;

            console.log('fetchAllCurrencies - API response:', {
                productsCount: products?.length || 0,
                retryCount,
                products: products?.map(p => ({
                    code: p.code,
                    plansCount: p.plans?.length || 0,
                    currencies: p.plans?.map(plan => plan.currency) || []
                }))
            });

            if (!products || products.length === 0) {
                console.warn('No plan products returned from API');
                // Retry with increasing delays
                if (retryCount < 2) {
                    setTimeout(() => fetchAllCurrencies(retryCount + 1), (retryCount + 1) * 1000);
                }
                return;
            }

            setAllPlanProducts(products); // Store all plans for comparison

            // Get unique currencies from all plans across all products
            const allCurrencies = new Set<string>();
            products.forEach((product: PlanProduct) => {
                if (product.plans && product.plans.length > 0) {
                    product.plans.forEach((plan: Plan) => {
                        if (plan.currency) {
                            allCurrencies.add(plan.currency);
                        }
                    });
                }
            });
            const currencies = Array.from(allCurrencies).sort();

            console.log('fetchAllCurrencies - Extracted currencies:', currencies, 'retryCount:', retryCount);

            if (currencies.length > 0) {
                // If we only got one currency, try to discover other currencies by fetching with explicit currency params
                if (currencies.length === 1 && retryCount < 3) {
                    console.log('Only one currency found, trying to discover other currencies...', currencies);

                    // Try fetching with known currencies to discover all available ones
                    const knownCurrencies = ['USD', 'BRL', 'EUR'];
                    const discoveredCurrencies = new Set<string>(currencies);

                    // Try fetching with other currencies in parallel
                    const currencyPromises = knownCurrencies
                        .filter(c => c !== currencies[0])
                        .map(async (currency) => {
                            try {
                                const testResponse = await live4tvPlanService.getPlanProducts(profile.token, currency, undefined);
                                const testProducts = testResponse.data.planProducts;
                                testProducts?.forEach((product: PlanProduct) => {
                                    product.plans?.forEach((plan: Plan) => {
                                        if (plan.currency) {
                                            discoveredCurrencies.add(plan.currency);
                                        }
                                    });
                                });
                            } catch (e) {
                                // Ignore errors for currency discovery
                            }
                        });

                    await Promise.all(currencyPromises);

                    const allDiscoveredCurrencies = Array.from(discoveredCurrencies).sort();
                    console.log('Discovered currencies:', allDiscoveredCurrencies);

                    if (allDiscoveredCurrencies.length > currencies.length) {
                        // Found more currencies, use them
                        setAvailableCurrencies(allDiscoveredCurrencies);
                        if (!selectedCurrency) {
                            setSelectedCurrency(getDefaultCurrency(allDiscoveredCurrencies));
                        }
                        return;
                    }

                    // If discovery didn't help, retry with delay
                    if (retryCount < 2) {
                        setTimeout(() => fetchAllCurrencies(retryCount + 1), (retryCount + 1) * 1500);
                        return;
                    }
                }

                // Update currencies if we have them
                setAvailableCurrencies(currencies);

                // Set initial currency if not already set, using location-based default
                if (!selectedCurrency) {
                    setSelectedCurrency(getDefaultCurrency(currencies));
                }
            } else {
                console.warn('No currencies found in plan products');
                // Retry with increasing delays
                if (retryCount < 2) {
                    setTimeout(() => fetchAllCurrencies(retryCount + 1), (retryCount + 1) * 1000);
                }
            }
        } catch (e) {
            console.error('Failed to fetch currencies:', e);
            // Retry with increasing delays on error
            if (retryCount < 2) {
                setTimeout(() => fetchAllCurrencies(retryCount + 1), (retryCount + 1) * 1500);
            }
        }
    }

    const fetchAvailablePlans = async () => {
        if (activePlans.length > 0) {
            return; // Don't fetch plans if user has active subscriptions
        }
        setPlanLoading(true);
        try {
            // Fetch plan products with plans filtered by selected currency and billing cycle
            const response = await live4tvPlanService.getPlanProducts(profile.token, selectedCurrency, selectedBillingCycle);
            const products = response.data.planProducts;
            setPlanProducts(products);
            setHasPreviousSubscriptions(response.data.hasPreviousSubscriptions);
        } catch (e) {
            console.error('Failed to fetch plan products:', e);
        } finally {
            setPlanLoading(false);
        }
    }

    useEffect(() => {
        if (selectedCurrency || selectedBillingCycle) {
            fetchAvailablePlans();
        }
    }, [selectedCurrency, selectedBillingCycle]);

    // Update currencies when billing cycle changes (in case different cycles have different currencies)
    useEffect(() => {
        if (selectedBillingCycle && activePlans.length === 0) {
            fetchAllCurrencies();
        }
    }, [selectedBillingCycle]);

    const handleManagePlan = () => {
        window.open(`${stripe.dashboard_url}?prefilled_email=${profile.email}`, '_blank');
    }

    const isTrialAvailable = (plan: Plan): boolean => {
        // Trial is available if:
        // 1. User has no active subscriptions (new user or returning user without active plan)
        // 2. User has never had any previous subscriptions (any status)
        // 3. Plan has a trial period defined (startingCycles > 0)
        return activePlans.length === 0 && !hasPreviousSubscriptions && plan.startingCycles > 0;
    }

    const handleSubscribe = async (planProduct: PlanProduct) => {
        // Find the plan within this product that matches selected currency and billing cycle
        const plan = planProduct.plans.find((p: Plan) =>
            p.currency === selectedCurrency && p.billingCycle === selectedBillingCycle
        );

        if (!plan) {
            alert(t('No plan available for the selected currency and billing cycle.'));
            return;
        }

        // Track subscription initiation event
        await gtm.trackEvent('plan_subscription_initiated', {
            plan_code: plan.code,
            plan_product_code: planProduct.code,
            currency: plan.currency,
            billing_cycle: plan.billingCycle,
            price: plan.price,
            starting_price: plan.startingPrice,
            starting_cycles: plan.startingCycles,
            has_trial: isTrialAvailable(plan),
        });

        setLoading(true);
        try {
            const successUrl = `${window.location.origin}/#/subscription/success`;
            const cancelUrl = `${window.location.origin}/#/subscription/cancel`;

            const response = await live4tvPlanService.createCheckoutSession(
                profile.token,
                plan.code,
                plan.currency,
                successUrl,
                cancelUrl
            );

            if (response.data.checkoutUrl) {
                window.location.href = response.data.checkoutUrl;
            }
        } catch (e) {
            console.error('Failed to create checkout session:', e);
            alert(t('Failed to create checkout session. Please try again.'));
            setLoading(false);
        }
    }

    const formatPrice = (price: number, currency: string): string => {
        if (currency === 'BRL') {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(price);
        }
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(price);
    }

    const translateFeature = (feature: {key: string, params?: {[key: string]: string | number}}): string => {
        const { key, params } = feature;

        // Special handling for resolution to format type properly
        if (key === 'plan.features.resolution' && params) {
            const resolution = params.resolution as string;
            const type = params.type as string;
            let formattedType = '';
            if (type === 'full_hd') {
                formattedType = ' (full HD)';
            } else if (type === '4k') {
                formattedType = ' (4K)';
            }
            return t(key, { resolution }) + formattedType;
        }

        // Translate the feature key with parameters
        if (params) {
            return t(key, params);
        }
        return t(key);
    }

    const getPlanTitle = (planProduct: PlanProduct): string => {
        return planProduct.code.charAt(0).toUpperCase() + planProduct.code.slice(1);
    }

    const isUserInBrazil = (): boolean => {
        try {
            // Check timezone - Brazil uses America/Sao_Paulo, America/Manaus, America/Belem, etc.
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone.includes('America/Sao_Paulo') ||
                timezone.includes('America/Manaus') ||
                timezone.includes('America/Belem') ||
                timezone.includes('America/Fortaleza') ||
                timezone.includes('America/Recife') ||
                timezone.includes('America/Araguaina') ||
                timezone.includes('America/Maceio') ||
                timezone.includes('America/Bahia') ||
                timezone.includes('America/Campo_Grande') ||
                timezone.includes('America/Cuiaba') ||
                timezone.includes('America/Santarem') ||
                timezone.includes('America/Porto_Velho') ||
                timezone.includes('America/Boa_Vista') ||
                timezone.includes('America/Rio_Branco') ||
                timezone.includes('America/Noronha')) {
                console.log('getDefaultCurrency - timezone includes America/Sao_Paulo:', timezone);
                return true;
            }

            // Check browser locale
            const locale = navigator.language || (navigator as any).userLanguage;
            if (locale && locale.toLowerCase().includes('pt-br')) {
                console.log('getDefaultCurrency - locale includes pt-br:', locale);
                return true;
            }

            // Check if locale is Portuguese (pt) - could be Brazil or Portugal, but default to Brazil assumption
            if (locale && locale.toLowerCase().startsWith('pt')) {
                console.log('getDefaultCurrency - locale:', locale);
                return true;
            }

            return false;
        } catch (e) {
            // If detection fails, default to false (assume outside Brazil)
            return false;
        }
    }

    const getDefaultCurrency = (availableCurrencies: string[]): string => {
        if (availableCurrencies.length === 0) {
            return '';
        }

        const isBrazil = isUserInBrazil();
        console.log('getDefaultCurrency - isBrazil:', isBrazil);

        // If user is in Brazil, prefer BRL, otherwise prefer USD
        if (isBrazil) {
            // Prefer BRL if available
            if (availableCurrencies.includes('BRL')) {
                return 'BRL';
            }
        } else {
            // Prefer USD if available for users outside Brazil
            if (availableCurrencies.includes('USD')) {
                return 'USD';
            }
        }

        // Fallback to first available currency
        return availableCurrencies[0];
    }

    const getPlanForProduct = (planProduct: PlanProduct): Plan | undefined => {
        return planProduct.plans.find((p: Plan) =>
            p.currency === selectedCurrency && p.billingCycle === selectedBillingCycle
        );
    }

    const getMonthlyPlanForProduct = (planProduct: PlanProduct): Plan | undefined => {
        // First try to find in the current product's plans (in case it has both monthly and yearly)
        const monthlyPlan = planProduct.plans.find((p: Plan) =>
            p.currency === selectedCurrency && p.billingCycle === 'monthly'
        );
        if (monthlyPlan) {
            return monthlyPlan;
        }
        // If not found, look in allPlanProducts (which contains all plans without filtering)
        const allProduct = allPlanProducts.find((p: PlanProduct) => p.code === planProduct.code);
        if (allProduct) {
            return allProduct.plans.find((p: Plan) =>
                p.currency === selectedCurrency && p.billingCycle === 'monthly'
            );
        }
        return undefined;
    }

    const renderSubscribeButton = (planProduct: PlanProduct) => (
        <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => handleSubscribe(planProduct)}
            disabled={loading}
        >
            {loading ? t('Loading...') : t('Start Trial')}
        </Button>
    )

    return (
        <Page desc="" title={t('Subscription')}>
            <Grid container direction="column" spacing={5} alignItems="center">

                {activePlans.length > 0 ? (
                    <Grid item container alignItems="center" direction="row" spacing={3} xs={12}>
                        {activePlans.map((plan: Subscription) => (
                            <Grid item xs={4}>
                                <Card key={plan.code}>
                                    <CardContent>
                                        <Typography variant="h5" component="div">
                                            {t('Subscription')}
                                        </Typography>

                                        <Typography sx={{mb: 1.5}} color="text.secondary">
                                            {t(plan.status)}
                                        </Typography>
                                    </CardContent>
                                    <CardActions>
                                        <Button
                                            size="small"
                                            color="secondary"
                                            variant="contained"
                                            onClick={() => handleManagePlan() }
                                            startIcon={<GrConfigure />}
                                        >
                                            {t('Manage Subscription')}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Fragment>
                        <Grid item xs={12}>
                            <Alert severity="info" className={classNames(classes.alert)}>
                                {t("You need an active Live4.tv account to start streaming")}<br />
                                {t("Cheaper than a pizza!")}
                            </Alert>
                        </Grid>

                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" gap={2} mb={3}>
                                <FormControl style={{minWidth: 150}}>
                                    <InputLabel>{t('Currency')}</InputLabel>
                                    <Select
                                        value={selectedCurrency}
                                        label={t('Currency')}
                                        onChange={(e) => setSelectedCurrency(e.target.value)}
                                    >
                                        {availableCurrencies.map((currency: string) => (
                                            <MenuItem key={currency} value={currency}>
                                                {currency}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" mb={4}>
                                <Tabs
                                    value={selectedBillingCycle}
                                    onChange={(e, newValue) => setSelectedBillingCycle(newValue)}
                                    sx={{
                                        '& .MuiTab-root': {
                                            minWidth: 150,
                                            fontSize: '1rem',
                                            fontWeight: 500,
                                        },
                                        '& .Mui-selected': {
                                            color: theme.palette.primary.main,
                                        },
                                        '& .MuiTabs-indicator': {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                    }}
                                >
                                    <Tab label={t('Monthly')} value="monthly" />
                                    <Tab label={t('Yearly')} value="yearly" />
                                </Tabs>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            {planLoading ? (
                                <Box display="flex" justifyContent="center">
                                    <CircularProgress />
                                </Box>
                            ) : planProducts.length > 0 ? (
                                <Grid container spacing={3} justifyContent="center">
                                    {planProducts.map((planProduct: PlanProduct, index: number) => {
                                        const plan = getPlanForProduct(planProduct);
                                        const isPopular = planProduct.code === 'professional';

                                        // Skip if no plan matches the selected currency/billing cycle
                                        if (!plan) {
                                            return null;
                                        }

                                        return (
                                            <Grid item xs={12} sm={6} md={3} key={planProduct.code}>
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        position: 'relative',
                                                        boxShadow: isPopular ? 4 : 1,
                                                        border: isPopular ? `2px solid ${theme.palette.primary.main}` : 'none',
                                                    }}
                                                >
                                                    {isPopular && (
                                                        <Chip
                                                            label={t('Most Popular')}
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 8,
                                                                right: 8,
                                                                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#e0e0e0',
                                                                color: theme.palette.text.primary,
                                                                fontSize: '0.75rem',
                                                            }}
                                                        />
                                                    )}
                                                    <CardContent sx={{flexGrow: 1, pb: 1}}>
                                                        <Typography variant="h6" component="div" gutterBottom>
                                                            {getPlanTitle(planProduct)}
                                                        </Typography>
                                                        {isTrialAvailable(plan) && (
                                                            <Chip
                                                                label={t('{{days}} days free', {days: plan.startingCycles})}
                                                                size="small"
                                                                sx={{
                                                                    mb: 1.5,
                                                                    backgroundColor: theme.palette.success.main,
                                                                    color: 'white',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.75rem',
                                                                }}
                                                            />
                                                        )}
                                                        {plan.billingCycle === 'yearly' ? (
                                                            <Box sx={{mb: 2}}>
                                                                {(() => {
                                                                    const monthlyPlan = getMonthlyPlanForProduct(planProduct);
                                                                    const monthlyEquivalent = plan.price / 12;
                                                                    return (
                                                                        <>
                                                                            <Box display="flex" alignItems="center" gap={1} sx={{mb: 0.5}}>
                                                                                {monthlyPlan && (
                                                                                    <Typography
                                                                                        variant="h6"
                                                                                        component="span"
                                                                                        sx={{
                                                                                            textDecoration: 'line-through',
                                                                                            color: theme.palette.text.secondary,
                                                                                            fontSize: '1.1rem'
                                                                                        }}
                                                                                    >
                                                                                        {formatPrice(monthlyPlan.price, plan.currency)}
                                                                                    </Typography>
                                                                                )}
                                                                                <Typography variant="h5" component="span" sx={{color: theme.palette.text.primary}}>
                                                                                    {formatPrice(monthlyEquivalent, plan.currency)}
                                                                                </Typography>
                                                                                <Typography variant="body2" component="span" color="text.secondary">
                                                                                    {t('per')} {t('month')}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                {t('Charged annually')}: {formatPrice(plan.price, plan.currency)}
                                                                            </Typography>
                                                                        </>
                                                                    );
                                                                })()}
                                                            </Box>
                                                        ) : (
                                                            <>
                                                                <Typography variant="h5" component="div" sx={{mb: 2, color: theme.palette.text.primary}}>
                                                                    {formatPrice(plan.price, plan.currency)}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                                                    {t('per')} {t('month')}
                                                                </Typography>
                                                            </>
                                                        )}
                                                        <Box sx={{mb: 3}}>
                                                            {renderSubscribeButton(planProduct)}
                                                        </Box>
                                                        <List dense sx={{p: 0}}>
                                                            {(planProduct.features || []).map((feature, idx: number) => (
                                                                <ListItem key={idx} sx={{px: 0, py: 0.5}}>
                                                                    <ListItemIcon sx={{minWidth: 32}}>
                                                                        <CheckCircleIcon sx={{color: theme.palette.text.secondary, fontSize: '0.875rem'}} />
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={translateFeature(feature)}
                                                                        primaryTypographyProps={{
                                                                            variant: 'body2',
                                                                            style: {fontSize: '0.875rem'}
                                                                        }}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </CardContent>
                                                    <CardActions sx={{p: 2, pt: 0}}>
                                                        {renderSubscribeButton(planProduct)}
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            ) : (
                                <Typography align="center">{t('No plans available')}</Typography>
                            )}
                        </Grid>
                    </Fragment>
                )}
            </Grid>

        </Page>
    );
}

const mapStateToProps = function (state: any) {
    return {
        profile: state.user.profile,
    }
}

function mapDispatchToProps(dispatch: Dispatch) {
    return {};
}

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(PlanPage));
